/**
 * leadImport.service.ts
 *
 * Posts spreadsheet rows the browser has already parsed and mapped.
 *
 * The file itself is never uploaded. The client sends batches of mapped rows, so
 * no request approaches the payload ceiling and the server needs no staging
 * table. The server caps a batch at 250 rows; BATCH_SIZE stays under that and
 * also sets how often the progress bar advances.
 */
import { apiClient } from './apiClient';
import type { ImportRowPayload } from '@/lib/leadImport';
import type { ParsedCsv } from '@/lib/csv';
import { uploadFile } from './uploads.service';

export const BATCH_SIZE = 200;

/** Ceiling on one upload, matched to the message shown when a file is too big. */
export const MAX_ROWS = 5000;
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

export type ImportRowStatus = 'imported' | 'skipped' | 'failed';

export interface LeadImportRowResult {
  /** 1-based row number in the user's own file, so they can find it. */
  row: number;
  name: string;
  status: ImportRowStatus;
  /** Why it was skipped or failed. Null for an imported row. */
  reason: string | null;
  /** Changes made to import the row, e.g. a value that had to be shortened. */
  warnings: string[];
}

export interface LeadImportBatchResult {
  imported: number;
  skipped: number;
  failed: number;
  /** Only rows the user may need to act on — a clean batch reports none. */
  results: LeadImportRowResult[];
}

export interface LeadImportBatchInput {
  rows: ImportRowPayload[];
  skipDuplicates: boolean;
  leadSource: string;
  /** Count without writing anything — powers the pre-flight summary. */
  dryRun?: boolean;
  /** File row number of the first row in this batch. */
  rowOffset: number;
  /**
   * Catalog ids applied to any lead the file does not give one of its own,
   * keyed by field. Sent as chosen; the server checks each against the
   * account's own catalogs and refuses the batch if one no longer exists.
   */
  defaults?: Record<string, string>;
}

export function importLeadBatch(input: LeadImportBatchInput): Promise<LeadImportBatchResult> {
  return apiClient
    .post('/leads/import', {
      // The server wraps a flat row as {values} when `format` is absent, so the
      // flag is what tells it these rows already carry lookups and custom
      // fields rather than being plain field/value pairs.
      format: 2,
      rows: input.rows,
      skipDuplicates: input.skipDuplicates,
      leadSource: input.leadSource,
      dryRun: input.dryRun ?? false,
      rowOffset: input.rowOffset,
      defaults: input.defaults ?? {},
    })
    .then((r) => r.data.data as LeadImportBatchResult);
}

/** How many problem rows the on-screen table will render. */
export const DISPLAY_LIMIT = 200;

/** Running totals across every batch of one upload. */
export interface LeadImportTally {
  imported: number;
  skipped: number;
  failed: number;
  /** Bounded for rendering — do NOT build the recovery file from this. */
  results: LeadImportRowResult[];
  /**
   * Every row that was not imported, uncapped.
   *
   * Kept separately from `results` because the "rows that weren't added"
   * download is built from it: capping this list would hand the user a recovery
   * file that silently omits most of what they need to fix, which is worse than
   * offering no file at all. Each entry is two small fields, so even a
   * completely failed 5,000-row upload stays trivial in memory.
   */
  notAdded: { row: number; reason: string }[];
}

export function emptyTally(): LeadImportTally {
  return { imported: 0, skipped: 0, failed: 0, results: [], notAdded: [] };
}

export function addBatch(tally: LeadImportTally, batch: LeadImportBatchResult): LeadImportTally {
  const problems = batch.results.filter((r) => r.status !== 'imported');

  return {
    imported: tally.imported + batch.imported,
    skipped: tally.skipped + batch.skipped,
    failed: tally.failed + batch.failed,
    // Bounded so a file where every row has a problem cannot lock the browser
    // up rendering 5,000 table rows the user will never scroll through.
    results: tally.results.length >= DISPLAY_LIMIT
      ? tally.results
      : [...tally.results, ...batch.results].slice(0, DISPLAY_LIMIT),
    notAdded: [
      ...tally.notAdded,
      ...problems.map((r) => ({ row: r.row, reason: r.reason ?? '' })),
    ],
  };
}

/**
 * Read an .xls/.xlsx workbook on the server, into the same shape the browser's
 * CSV parser produces.
 *
 * CSV is parsed in the browser and never uploaded — that stays the fast, common
 * path. A workbook cannot be: no dependency can be installed in this workspace,
 * and there is no ZIP/inflate library in the tree to hand-roll an .xlsx reader
 * against. So it takes a round trip and comes back as ParsedCsv, which is why
 * every step after this one is identical for both formats.
 *
 * `uploadFile` picks multipart or a presigned S3 PUT by size on its own, so a
 * workbook near the 10 MB ceiling never hits the Lambda payload limit.
 */
export function parseSpreadsheet(file: File, sheet?: string): Promise<ParsedCsv> {
  return uploadFile(
    '/leads/import/parse',
    file,
    sheet ? { sheet } : undefined,
  ).then((r) => r.data.data as ParsedCsv);
}

/** Magic bytes for the two workbook containers we can read. */
const XLSX_SIGNATURE = [0x50, 0x4b, 0x03, 0x04];   // ZIP
const XLS_SIGNATURE = [0xd0, 0xcf, 0x11, 0xe0];    // OLE compound file

/**
 * Whether this file is a workbook, judged by its contents rather than its name.
 *
 * People rename spreadsheets to .csv surprisingly often, and the server sniffs
 * content too, so deciding here on the extension would send a real workbook
 * down the CSV path and produce nonsense rows instead of an error.
 */
export async function isSpreadsheet(file: File): Promise<boolean> {
  const head = new Uint8Array(await file.slice(0, 4).arrayBuffer());
  if (head.length < 4) return false;

  const matches = (signature: number[]) => signature.every((byte, i) => head[i] === byte);

  return matches(XLSX_SIGNATURE) || matches(XLS_SIGNATURE);
}
